import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { Chip } from "@connected-repo/ui-mui/data-display/Chip";
import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { TextField } from "@connected-repo/ui-mui/form/TextField";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { Card } from "@connected-repo/ui-mui/layout/Card";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { formatMinutes } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

export default function TeamsPage() {
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [debounced, setDebounced] = useState("");
	useEffect(() => {
		const t = setTimeout(() => setDebounced(search.trim()), 250);
		return () => clearTimeout(t);
	}, [search]);

	const { data, isLoading, error } = useQuery(
		orpc.listTeams.queryOptions({ input: { search: debounced || undefined, limit: 100, offset: 0 } }),
	);

	return (
		<Stack spacing={3}>
			<Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
				<Box>
					<Typography variant="h4" sx={{ fontWeight: 800 }}>
						Teams
					</Typography>
					<Typography color="text.secondary">
						{data ? `${data.total} team${data.total === 1 ? "" : "s"}` : "Client teams and their minute balances"}
					</Typography>
				</Box>
				<TextField
					placeholder="Search by name or subdomain"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ minWidth: { sm: 320 } }}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						},
					}}
				/>
			</Stack>

			{isLoading && <LoadingSpinner text="Loading teams…" />}
			{error && <Typography color="error">{error.message}</Typography>}

			{data && (
				<Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflowX: "auto" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Team</TableCell>
								<TableCell>Subdomain</TableCell>
								<TableCell>Owner</TableCell>
								<TableCell align="right">Members</TableCell>
								<TableCell align="right">Rooms</TableCell>
								<TableCell align="right">Minutes balance</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.rows.length === 0 && (
								<TableRow>
									<TableCell colSpan={6}>
										<Typography color="text.secondary" sx={{ py: 2 }}>
											No teams match.
										</Typography>
									</TableCell>
								</TableRow>
							)}
							{data.rows.map((t) => (
								<TableRow
									key={t.id}
									hover
									onClick={() => navigate(`/teams/${t.id}`)}
									sx={{ cursor: "pointer" }}
								>
									<TableCell>
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography sx={{ fontWeight: 600 }}>{t.name}</Typography>
											{t.personalTeamForUserId && (
												<Chip label="Personal" size="small" variant="outlined" />
											)}
										</Stack>
									</TableCell>
									<TableCell>{t.slug ?? <Typography color="text.disabled">—</Typography>}</TableCell>
									<TableCell>{t.ownerEmail ?? "—"}</TableCell>
									<TableCell align="right">{t.memberCount}</TableCell>
									<TableCell align="right">
										{t.roomCapacity > 0 ? t.roomCapacity : <Typography color="text.disabled">—</Typography>}
									</TableCell>
									<TableCell align="right">
										<Typography
											sx={{ fontWeight: 700, color: t.minutesBalance > 0 ? "success.main" : "text.secondary" }}
										>
											{formatMinutes(t.minutesBalance)}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>
			)}
		</Stack>
	);
}
